import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Upload,
  FileText,
  Users,
  Receipt,
  CheckCircle2,
  XCircle,
  Clock,
  FileUp,
  Loader2,
  AlertCircle,
  Trash2,
  Eye,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'

export const Route = createFileRoute('/upload')({
  component: UploadPage,
})

// Types
type FileType = 'invoices' | 'patients' | 'treatments'
type UploadStatus = 'pending' | 'processing' | 'completed' | 'failed'

interface UploadRecord {
  id: number
  filename: string
  fileType: FileType
  fileSize: number
  status: UploadStatus
  recordsProcessed: number
  recordsFailed: number
  uploadedAt: Date
  completedAt?: Date
  error?: string
}

// Mock upload history data
const mockUploadHistory: UploadRecord[] = [
  {
    id: 1,
    filename: 'Invoice Report 04-01-2026_15102.csv',
    fileType: 'invoices',
    fileSize: 245000,
    status: 'completed',
    recordsProcessed: 119,
    recordsFailed: 0,
    uploadedAt: new Date('2026-01-06T10:30:00'),
    completedAt: new Date('2026-01-06T10:32:00'),
  },
  {
    id: 2,
    filename: 'december25-patients.csv',
    fileType: 'patients',
    fileSize: 128000,
    status: 'completed',
    recordsProcessed: 342,
    recordsFailed: 3,
    uploadedAt: new Date('2026-01-04T14:20:00'),
    completedAt: new Date('2026-01-04T14:22:00'),
  },
  {
    id: 3,
    filename: 'treatment-updates.csv',
    fileType: 'treatments',
    fileSize: 56000,
    status: 'failed',
    recordsProcessed: 0,
    recordsFailed: 0,
    uploadedAt: new Date('2026-01-03T09:15:00'),
    error: 'Invalid column format',
  },
]

// File type config
const FILE_TYPE_CONFIG: Record<FileType, { icon: React.ElementType; label: string; description: string; color: string }> = {
  invoices: {
    icon: Receipt,
    label: 'Invoices',
    description: 'Upload invoice data from clinic POS system',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  patients: {
    icon: Users,
    label: 'Patients',
    description: 'Upload patient records with contact details',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  treatments: {
    icon: FileText,
    label: 'Treatments',
    description: 'Upload treatment master data or variations',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
}

// Status badge config
const STATUS_CONFIG: Record<UploadStatus, { icon: React.ElementType; label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { icon: Clock, label: 'Pending', variant: 'secondary' },
  processing: { icon: Loader2, label: 'Processing', variant: 'outline' },
  completed: { icon: CheckCircle2, label: 'Completed', variant: 'default' },
  failed: { icon: XCircle, label: 'Failed', variant: 'destructive' },
}

function UploadPage() {
  // State
  const [selectedFileType, setSelectedFileType] = React.useState<FileType>('invoices')
  const [isDragging, setIsDragging] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [isUploading, setIsUploading] = React.useState(false)
  const [uploadHistory] = React.useState<UploadRecord[]>(mockUploadHistory)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Stats
  const stats = {
    totalUploads: uploadHistory.length,
    successfulUploads: uploadHistory.filter(u => u.status === 'completed').length,
    failedUploads: uploadHistory.filter(u => u.status === 'failed').length,
    totalRecords: uploadHistory.reduce((acc, u) => acc + u.recordsProcessed, 0),
  }

  // Handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    const firstFile = files[0]
    if (files.length > 0 && firstFile) {
      validateAndSetFile(firstFile)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const firstFile = files?.[0]
    if (files && files.length > 0 && firstFile) {
      validateAndSetFile(firstFile)
    }
  }

  const validateAndSetFile = (file: File) => {
    // Check file type
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file')
      return
    }
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB')
      return
    }
    setSelectedFile(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    // Simulate upload
    await new Promise(resolve => setTimeout(resolve, 2000))
    setIsUploading(false)
    setSelectedFile(null)
    alert('Upload completed! (Demo mode - no actual upload)')
  }

  const handleClearFile = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const config = FILE_TYPE_CONFIG[selectedFileType]
  const FileTypeIcon = config.icon

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Upload Data"
        subtitle="Import CSV files to update clinic data"
      />

      <main className="flex-1 p-3 sm:p-4 lg:p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard
            title="Total Uploads"
            value={stats.totalUploads}
            icon={FileUp}
          />
          <StatsCard
            title="Successful"
            value={stats.successfulUploads}
            icon={CheckCircle2}
            className="text-success"
          />
          <StatsCard
            title="Failed"
            value={stats.failedUploads}
            icon={XCircle}
            className="text-destructive"
          />
          <StatsCard
            title="Records Processed"
            value={stats.totalRecords.toLocaleString()}
            icon={FileText}
          />
        </div>

        {/* Upload Section */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* File Upload Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload CSV File
              </CardTitle>
              <CardDescription>
                Select file type and upload your CSV file
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* File Type Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium">File Type</label>
                <Select
                  value={selectedFileType}
                  onValueChange={(v) => setSelectedFileType(v as FileType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FILE_TYPE_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <cfg.icon className="h-4 w-4" />
                          {cfg.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{config.description}</p>
              </div>

              {/* Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all',
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50',
                  selectedFile && 'border-success bg-success/5'
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {selectedFile ? (
                  <div className="space-y-2">
                    <div className={cn('w-12 h-12 mx-auto rounded-lg flex items-center justify-center', config.color)}>
                      <FileTypeIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleClearFile()
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="w-12 h-12 mx-auto rounded-lg bg-muted flex items-center justify-center">
                      <Upload className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        Drop your CSV file here
                      </p>
                      <p className="text-sm text-muted-foreground">
                        or click to browse
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Maximum file size: 10MB
                    </p>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <Button
                className="w-full"
                disabled={!selectedFile || isUploading}
                onClick={handleUpload}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload File
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* File Format Guide */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                File Format Guide
              </CardTitle>
              <CardDescription>
                Required columns for each file type
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(FILE_TYPE_CONFIG).map(([key, cfg]) => (
                <div
                  key={key}
                  className={cn(
                    'p-4 rounded-lg border transition-colors',
                    selectedFileType === key
                      ? 'border-primary bg-primary/5'
                      : 'border-border'
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={cn('p-2 rounded-lg', cfg.color)}>
                      <cfg.icon className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{cfg.label}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {key === 'invoices' && (
                      <p>Required: Invoice No, Invoice Date, Patient Name, Grand Total, Branch</p>
                    )}
                    {key === 'patients' && (
                      <p>Required: Name, Phone, NRIC, Email (optional), Address (optional)</p>
                    )}
                    {key === 'treatments' && (
                      <p>Required: Treatment Name, Category, Type, Base Price</p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Upload History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Upload History
            </CardTitle>
            <CardDescription>
              Recent file uploads and their status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Records</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploadHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No upload history yet
                    </TableCell>
                  </TableRow>
                ) : (
                  uploadHistory.map((record) => {
                    const typeConfig = FILE_TYPE_CONFIG[record.fileType]
                    const statusConfig = STATUS_CONFIG[record.status]
                    const StatusIcon = statusConfig.icon

                    return (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <typeConfig.icon className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate max-w-[200px]">{record.filename}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeConfig.color}>
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatFileSize(record.fileSize)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusConfig.variant} className="gap-1">
                            <StatusIcon className={cn('h-3 w-3', record.status === 'processing' && 'animate-spin')} />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {record.status === 'completed' ? (
                            <span>
                              {record.recordsProcessed}
                              {record.recordsFailed > 0 && (
                                <span className="text-destructive ml-1">
                                  ({record.recordsFailed} failed)
                                </span>
                              )}
                            </span>
                          ) : record.status === 'failed' ? (
                            <span className="text-destructive text-sm">{record.error}</span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDistanceToNow(record.uploadedAt, { addSuffix: true })}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  )
}

// Stats Card Component
function StatsCard({
  title,
  value,
  icon: Icon,
  className,
}: {
  title: string
  value: string | number
  icon: React.ElementType
  className?: string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-icon-beige">
            <Icon className={cn('h-5 w-5 text-foreground', className)} />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
